import React, { useState, useRef, useEffect } from 'react';
import Modal from './Modal';
import { CheckCircle, ChevronDown, Copy, Eye, EyeOff, Link, Plus, Upload } from 'lucide-react';
import { Checkbox } from './FieldComponents';
import '../styles/Customs.css';
import '../styles/CaseShareLinksModal.css';

// A share code is not an account credential. Keep the field a text input even
// when visually masked: switching it to password lets browser login heuristics
// pair it with unrelated, unowned search inputs elsewhere on the page.
const ShareCodeInput = ({ visible, style, ...props }) => {
    const supportsTextMask = typeof CSS !== 'undefined'
        && typeof CSS.supports === 'function'
        && CSS.supports('-webkit-text-security', 'disc');
    const needsNativeMask = !visible && !supportsTextMask;

    return (
        <input
            {...props}
            type={needsNativeMask ? 'password' : 'text'}
            autoComplete={needsNativeMask ? 'section-share-link new-password' : 'off'}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            data-lpignore="true"
            style={{ ...style, WebkitTextSecurity: !visible && supportsTextMask ? 'disc' : 'none' }}
        />
    );
};

const ShareModal = ({ onClose, onViewLinks, target, createShareableLink, shareLink, caseName }) => {
    const [expirationDays, setExpirationDays] = useState(7); // Default 7 days (preset)
    const [customExpiration, setCustomExpiration] = useState(''); // Custom days
    const [useCustom, setUseCustom] = useState(false);
    const [allowUpload, setAllowUpload] = useState(false); // New state for upload permission
    const [includeFutureUploads, setIncludeFutureUploads] = useState(false);
    const [allowProtectedDocuments, setAllowProtectedDocuments] = useState(false);
    const [protectedDocumentsConfirmed, setProtectedDocumentsConfirmed] = useState(false);
    const [protectedDocumentsIncluded, setProtectedDocumentsIncluded] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(true);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');
    const linkInputRef = useRef(null);
    const targetScope = JSON.stringify([
        target?.type, target?.key, target?.folderPath, target?.selectedKeys,
        target?.items?.map(item => item?.key), caseName,
    ]);
    const targetScopeRef = useRef({ key: targetScope });
    if (targetScopeRef.current.key !== targetScope) {
        targetScopeRef.current = { key: targetScope };
    }

    useEffect(() => {
        setAllowProtectedDocuments(false);
        setProtectedDocumentsConfirmed(false);
        setProtectedDocumentsIncluded(false);
        setWarning('');
        setError('');
        setLoading(false);
    }, [targetScope]);

    const isFolderTarget = target?.type === 'folder';
    const isMultiTarget = target?.type === 'multi';
    const targetLabel = isMultiTarget ? 'selected items' : isFolderTarget ? 'folder' : 'file';
    const titleLabel = isMultiTarget ? 'Selected Items' : isFolderTarget ? 'Folder' : 'File';

    const handleCreateLink = async () => {
        if (loading) return;
        if (allowProtectedDocuments && !protectedDocumentsConfirmed) return;
        const requestScope = targetScopeRef.current;
        setLoading(true);
        setError('');
        setWarning('');
        const days = useCustom ? parseFloat(customExpiration) : expirationDays;
        const safeDays = isNaN(days) || days <= 0 ? expirationDays : days;
        try {
            const result = await createShareableLink(
                safeDays,
                allowUpload,
                password.trim() || null,
                includeFutureUploads,
                allowProtectedDocuments,
                allowProtectedDocuments && protectedDocumentsConfirmed
            );
            if (targetScopeRef.current === requestScope) {
                setWarning(typeof result?.warning === 'string' ? result.warning : '');
                setProtectedDocumentsIncluded(result?.protected_documents_included === true);
            }
        } catch (createError) {
            if (targetScopeRef.current === requestScope) {
                setError(createError?.message || 'CaseDB could not create the link. Please try again.');
            }
        } finally {
            if (targetScopeRef.current === requestScope) setLoading(false);
        }
    };

    const canIncludeFutureUploads = target?.type === 'folder'
        || (target?.type === 'multi'
            && Array.isArray(target?.items)
            && target.items.some(item => item?.type === 'folder' || item?.is_folder));

    const copyToClipboard = () => {
        if (!shareLink) return;
        
        linkInputRef.current.select();
        document.execCommand('copy');
        setCopied(true);
        
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    return (
        <Modal
            title={`Share ${titleLabel}`}
            instructions={`Create a shareable link for ${target?.name === "{{Name}}" ? caseName ?? target?.name : target?.name}`}
            onClose={onClose}
            overflowy={true}
            header={onViewLinks ? (
                <div className='modal-header-actions'>
                    <div className="this-or-that-comp case-share-links-mode-switch" role="group" aria-label="Share link view">
                        <button
                            type="button"
                            className="tot-comp-option"
                            onClick={onViewLinks}
                            aria-pressed="false"
                            title="View existing share links"
                            disabled={loading}
                        >
                            <Link size={16} />
                        </button>
                        <button
                            type="button"
                            className="tot-comp-option active"
                            aria-pressed="true"
                            title="Create a new share link"
                        >
                            <Plus size={16} />
                        </button>
                    </div>
                </div>
            ) : undefined}
            footer={
                <div className='modal-footer-actions'>
                    {!shareLink && (
                        <button
                            type="button"
                            onClick={handleCreateLink} 
                            className="action"
                            disabled={loading || (allowProtectedDocuments && !protectedDocumentsConfirmed)}
                            title="Create share link"
                        >
                            {loading ? "Creating..." : "Create Link"}
                        </button>
                    )}
                    <button type="button" onClick={onClose} className="action alt" title="Close share link creator">Close</button>
                </div>
            }
        >
            <div className='modal-content-wrapper share-folder-modal-settings'>
                {!shareLink ? (
                    <>
                        <div className='form-group mid nm'>
                            <label className="subtext">Link Expiration</label>
                            <div className='filter-group'>
                                <div className='select-wrapper'>
                                    <select
                                        disabled={loading}
                                        value={useCustom ? 'custom' : String(expirationDays)}
                                        onChange={(e) => {
                                            if (e.target.value === 'custom') {
                                                setUseCustom(true);
                                            } else {
                                                setUseCustom(false);
                                                setExpirationDays(parseInt(e.target.value));
                                            }
                                        }}
                                        className='filter-select'
                                    >
                                        <option value="1">1 day</option>
                                        <option value="3">3 days</option>
                                        <option value="7">7 days</option>
                                        <option value="14">14 days</option>
                                        <option value="30">30 days</option>
                                        <option value="custom">Custom...</option>
                                    </select>
                                    <ChevronDown className='select-chevron' size={16} />
                                </div>
                            </div>
                            {useCustom && (
                                <input
                                    disabled={loading}
                                    type="number"
                                    min={0.01}
                                    step={0.01}
                                    placeholder="Days"
                                    value={customExpiration}
                                    onChange={(e) => setCustomExpiration(e.target.value)}
                                    style={{ width: 120 }}
                                />
                            )}
                            {useCustom && (
                                <p className="subtext">
                                    Enter fractional days (e.g. 0.5 for 12 hours). Max recommended 90.
                                </p>
                            )}
                        </div>

                        <div className='form-group mid'>
                            <label className="subtext" htmlFor="share-access-code">Optional password</label>
                            <form
                                aria-label="Share link access code"
                                autoComplete="off"
                                data-form-type="other"
                                data-1p-ignore="true"
                                data-lpignore="true"
                                data-bwignore="true"
                                onSubmit={event => event.preventDefault()}
                                style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 8, margin: 0, width: '100%' }}
                            >
                            <ShareCodeInput
                                id="share-access-code"
                                disabled={loading}
                                visible={showPassword}
                                name="share-access-code"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Set a password (leave blank for none)"
                                style={{ flex: 1, minWidth: 0 }}
                            />
                            <button type="button" className="action-micro" aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(value => !value)}>
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                            </form>
                            {password && (
                                <p className="subtext">
                                    This password is only for the shared link, not your CaseDB login.
                                </p>
                            )}
                        </div>

                        {/* Only show upload option for folders */}
                        {isFolderTarget && (
                            <>
                                <Checkbox
                                    label="Allow uploads to this folder"
                                    value={allowUpload}
                                    disabled={loading}
                                    onChange={() => setAllowUpload(value => !value)}
                                />
                                {allowUpload && (
                                    <p className="small-text" style={{ color: '#ff9800', margin: '0' }}>
                                        <Upload size={16} style={{ verticalAlign: 'text-bottom' }}/>
                                        Anyone with this link will be able to upload files to this folder.
                                    </p>
                                )}
                            </>
                        )}

                        {canIncludeFutureUploads && (
                            <Checkbox
                                label="Allow future uploads to appear in this link"
                                hint="When selected, files added to the shared folder later will also be visible. Leave this off to share only the files that are here now."
                                value={includeFutureUploads}
                                disabled={loading}
                                space
                                onChange={() => setIncludeFutureUploads(value => !value)}
                            />
                        )}

                        <div className="form-group mid">
                            <label className="subtext">Protected content</label>
                            <Checkbox
                                label="Include protected folders and files"
                                ariaLabel="Include protected folders and files"
                                value={allowProtectedDocuments}
                                disabled={loading}
                                onChange={() => {
                                    setAllowProtectedDocuments(value => !value);
                                    setProtectedDocumentsConfirmed(false);
                                }}
                            />
                            {allowProtectedDocuments ? (
                                <>
                                    <p id="share-protected-warning" className="small-text" role="alert" style={{ color: 'var(--priority-color)', margin: '5px 0 5px 0' }}>
                                        Protected folders such as Attorney Notes may contain confidential material. Anyone with this link can access protected content within your selection. This applies only to this link and does not allow uploads into protected folders.
                                    </p>
                                    <Checkbox
                                        label="I understand and want to share the selected protected content"
                                        ariaLabel="I understand and want to share the selected protected content"
                                        ariaDescribedBy="share-protected-warning"
                                        value={protectedDocumentsConfirmed}
                                        disabled={loading}
                                        onChange={() => setProtectedDocumentsConfirmed(value => !value)}
                                    />
                                </>
                            ) : (
                                <p className="small-text" style={{ color: 'var(--priority-color)', margin: '5px 0 5px 0' }}>
                                    Attorney Notes files are excluded unless you explicitly include and confirm them for this link.
                                </p>
                            )}
                        </div>

                        <p className="small-text" style={{ color: 'var(--subtext-color)', margin: '10px 0 0 0' }}>
                            Anyone with this link will be able to access the {targetLabel} without logging in.
                        </p>
                        {error && (
                            <p className="small-text" style={{ color: 'var(--priority-color)', margin: '10px 0 0 0' }}>
                                {error}
                            </p>
                        )}
                    </>
                ) : (
                    <>
                        <div className='form-group mid nm'>
                            <label className="subtext">Shareable Link (Expires in {useCustom ? customExpiration || expirationDays : expirationDays} days)</label>
                            <div className="share-link-container" style={{ display: 'flex', position: 'relative' }}>
                                <input
                                    ref={linkInputRef}
                                    type="text"
                                    value={shareLink}
                                    readOnly
                                    style={{ width: '100%', paddingRight: '40px' }}
                                />
                                <button 
                                    onClick={copyToClipboard} 
                                    style={{ 
                                        position: 'absolute', 
                                        right: '5px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--text-color)'
                                    }}
                                    title="Copy to clipboard"
                                >
                                    {copied ? <CheckCircle size={18} color="green" /> : <Copy size={18} />}
                                </button>
                            </div>
                        </div>
                        <p className="subtext">
                            This link will automatically expire after {useCustom ? customExpiration || expirationDays : expirationDays} days.
                        </p>
                        {password.trim() && (
                            <div className="form-group mid nm">
                                <label className="subtext" htmlFor="created-share-access-code">Link password</label>
                                <form
                                    aria-label="Created share link access code"
                                    autoComplete="off"
                                    data-form-type="other"
                                    data-1p-ignore="true"
                                    data-lpignore="true"
                                    data-bwignore="true"
                                    onSubmit={event => event.preventDefault()}
                                    style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 8, margin: 0, width: '100%' }}
                                >
                                <ShareCodeInput
                                    id="created-share-access-code"
                                    name="created-share-access-code"
                                    visible={showPassword}
                                    value={password.trim()}
                                    readOnly
                                    onFocus={event => event.target.select()}
                                    style={{ flex: 1, minWidth: 0 }}
                                />
                                <button type="button" className="action-micro" aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(value => !value)}>
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                                </form>
                                <p className="subtext">Copy this password before closing. It cannot be shown again after this window closes.</p>
                            </div>
                        )}

                        {allowUpload && (
                            <p className="subtext" style={{ color: '#ff9800' }}>
                                <Upload size={16} style={{ marginRight: '5px', verticalAlign: 'text-bottom' }}/>
                                Recipients can upload files to this folder.
                            </p>
                        )}
                        {canIncludeFutureUploads && includeFutureUploads && (
                            <p className="subtext">
                                Files added to this folder later will appear in this link.
                            </p>
                        )}
                    </>
                )}
                {warning && <p className="subtext" role="status">{warning}</p>}
                {protectedDocumentsIncluded && (
                    <p className="subtext" role="status" style={{ color: 'var(--priority-color)' }}>
                        Protected content within your selection is included in this link. Share it only with intended recipients.
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default ShareModal;
